(() => {
  const KEY = '__fsDialRevealInitialized';
  if (window[KEY]) return;
  window[KEY] = true;

  const instances = new WeakMap();
  let webglSupport;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const lerp = (start, end, progress) => start + (end - start) * progress;
  const easeInOutSine = (progress) => -(Math.cos(Math.PI * progress) - 1) / 2;

  const supportsWebGL = () => {
    if (typeof webglSupport === 'boolean') return webglSupport;

    try {
      const canvas = document.createElement('canvas');
      webglSupport = !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      );
    } catch (error) {
      webglSupport = false;
    }

    return webglSupport;
  };

  const canEnhance = () => {
    if (typeof window.matchMedia !== 'function') return false;
    if (typeof window.THREE === 'undefined') return false;
    if (!supportsWebGL()) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
  };

  const resolveInteractionMode = () => {
    if (typeof window.matchMedia !== 'function') return 'scroll';
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches ? 'hover' : 'scroll';
  };

  const readConfig = (section) => {
    const configEl = section.querySelector('[data-fs-dial-config]');
    if (!configEl) return null;

    try {
      const parsed = JSON.parse(configEl.textContent || '{}');
      if (!parsed.image1Url || !parsed.image2Url) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const loadTexture = (loader, url) =>
    new Promise((resolve, reject) => {
      loader.load(
        url,
        (texture) => resolve(texture),
        undefined,
        (error) => reject(error || new Error(`Texture load failed: ${url}`))
      );
    });

  const initSection = (section) => {
    if (!section || instances.has(section)) return;

    const media = section.querySelector('[data-fs-dial-media]');
    const config = readConfig(section);
    if (!media || !config) return;

    let disposed = false;
    let started = false;
    let rafId = 0;
    let observer = null;
    let visibilityObserver = null;
    let renderer = null;
    let geometry = null;
    let material = null;
    let texture1 = null;
    let texture2 = null;
    let resizeHandler = null;
    let onVisibilityChange = null;
    let onPointerEnter = null;
    let onPointerLeave = null;
    let onPointerMove = null;
    let interactionMode = 'hover';
    let hovering = false;
    let isNearViewport = false;
    let isDocumentVisible =
      typeof document.visibilityState === 'undefined' ? true : document.visibilityState !== 'hidden';
    let hasRenderedFrame = false;
    let clock = null;
    let targetRadius = 0.0;
    let currentRadius = 0.0;
    const mouseTarget = { x: 0.5, y: 0.5 };
    const mouseCurrent = { x: 0.5, y: 0.5 };

    const cleanup = () => {
      disposed = true;

      if (observer) observer.disconnect();
      if (visibilityObserver) visibilityObserver.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeHandler) window.removeEventListener('resize', resizeHandler);
      if (onVisibilityChange) document.removeEventListener('visibilitychange', onVisibilityChange);
      if (onPointerEnter) section.removeEventListener('mouseenter', onPointerEnter);
      if (onPointerLeave) section.removeEventListener('mouseleave', onPointerLeave);
      if (onPointerMove) section.removeEventListener('mousemove', onPointerMove);

      section.classList.remove('is-webgl-active');
      section.classList.remove('is-webgl-rendered');

      if (renderer && renderer.domElement && renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }

      if (renderer) renderer.dispose();
      if (geometry) geometry.dispose();
      if (material) material.dispose();
      if (texture1) texture1.dispose();
      if (texture2) texture2.dispose();

      instances.delete(section);
    };

    const start = async () => {
      if (started || disposed || !canEnhance()) return;
      started = true;

      const THREE = window.THREE;
      const loader = new THREE.TextureLoader();
      loader.setCrossOrigin('anonymous');

      try {
        [texture1, texture2] = await Promise.all([
          loadTexture(loader, config.image1Url),
          loadTexture(loader, config.image2Url)
        ]);
      } catch (error) {
        return;
      }

      if (disposed) {
        if (texture1) texture1.dispose();
        if (texture2) texture2.dispose();
        return;
      }

      [texture1, texture2].forEach((texture) => {
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.generateMipmaps = false;
      });

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0x000000, 0);
      renderer.domElement.setAttribute('aria-hidden', 'true');
      media.appendChild(renderer.domElement);

      const uniforms = {
        uTexture1: { value: texture1 },
        uTexture2: { value: texture2 },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uRadius: { value: 0.0 },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uImageRes: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0.0 }
      };

      const sourceImage = texture1.image || {};
      uniforms.uImageRes.value.set(
        sourceImage.naturalWidth || sourceImage.videoWidth || sourceImage.width || 1,
        sourceImage.naturalHeight || sourceImage.videoHeight || sourceImage.height || 1
      );

      const vertexShader = `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `;

      const fragmentShader = `
        precision highp float;
        uniform sampler2D uTexture1;
        uniform sampler2D uTexture2;
        uniform vec2 uMouse;
        uniform float uRadius;
        uniform vec2 uResolution;
        uniform vec2 uImageRes;
        uniform float uTime;
        varying vec2 vUv;

        vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
        vec2 mod289(vec2 x){return x-floor(x*(1.0/289.0))*289.0;}
        vec3 permute(vec3 x){return mod289(((x*34.0)+1.0)*x);}

        float snoise(vec2 v){
          const vec4 C=vec4(0.211324865405187,0.366025403784439,-0.577350269189626,0.024390243902439);
          vec2 i=floor(v+dot(v,C.yy));
          vec2 x0=v-i+dot(i,C.xx);
          vec2 i1=(x0.x>x0.y)?vec2(1.0,0.0):vec2(0.0,1.0);
          vec4 x12=x0.xyxy+C.xxzz;
          x12.xy-=i1;
          i=mod289(i);
          vec3 p=permute(permute(i.y+vec3(0.0,i1.y,1.0))+i.x+vec3(0.0,i1.x,1.0));
          vec3 m=max(0.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.0);
          m=m*m;
          m=m*m;
          vec3 x=2.0*fract(p*C.www)-1.0;
          vec3 h=abs(x)-0.5;
          vec3 ox=floor(x+0.5);
          vec3 a0=x-ox;
          m*=1.79284291400159-0.85373472095314*(a0*a0+h*h);
          vec3 g;
          g.x=a0.x*x0.x+h.x*x0.y;
          g.yz=a0.yz*x12.xz+h.yz*x12.yw;
          return 130.0*dot(m,g);
        }

        void main() {
          vec2 uv = vUv;
          float screenAspect = uResolution.x / uResolution.y;
          float imageAspect = uImageRes.x / uImageRes.y;

          vec2 scale = vec2(1.0);
          if (screenAspect > imageAspect) {
            scale.x = imageAspect / screenAspect;
          } else {
            scale.y = screenAspect / imageAspect;
          }
          scale *= 0.85;

          vec2 centeredUv = (uv - 0.5) / scale + 0.5;
          if (centeredUv.x < 0.0 || centeredUv.x > 1.0 || centeredUv.y < 0.0 || centeredUv.y > 1.0) {
            discard;
          }

          vec2 diff = uv - uMouse;
          diff.x *= screenAspect;
          float dist = length(diff);
          float noise = snoise(uv * 4.0 + uTime * 0.3) * 0.04;
          float edge = uRadius * 0.28;
          float mask = smoothstep(edge + 0.02, edge - 0.03, dist + noise);

          float boundaryProx = clamp(1.0 - abs(dist - edge) / 0.08, 0.0, 1.0);
          vec2 dispOffset = vec2(
            snoise(uv * 8.0 + uTime * 0.5),
            snoise(uv * 8.0 + uTime * 0.5 + 100.0)
          ) * 0.015 * boundaryProx * uRadius;

          vec2 uv1 = clamp(centeredUv + dispOffset, vec2(0.0), vec2(1.0));
          vec2 uv2 = clamp(centeredUv - dispOffset, vec2(0.0), vec2(1.0));

          vec4 color1 = texture2D(uTexture1, uv1);
          vec4 color2 = texture2D(uTexture2, uv2);

          gl_FragColor = mix(color1, color2, mask);
        }
      `;

      geometry = new THREE.PlaneGeometry(2, 2);
      material = new THREE.ShaderMaterial({
        uniforms,
        vertexShader,
        fragmentShader,
        transparent: true,
        depthTest: false
      });

      scene.add(new THREE.Mesh(geometry, material));

      const updateViewportState = () => {
        const rect = section.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const threshold = 200;
        isNearViewport = rect.bottom >= -threshold && rect.top <= viewportHeight + threshold;
      };

      const shouldAnimate = () => {
        if (disposed || !renderer || !isNearViewport || !isDocumentVisible) return false;
        if (interactionMode === 'scroll') return true;
        return hovering || currentRadius > 0.001 || targetRadius > 0.001;
      };

      const scheduleAnimation = () => {
        if (rafId || !shouldAnimate()) return;
        rafId = window.requestAnimationFrame(animate);
      };

      const syncAnimationState = () => {
        if (shouldAnimate()) {
          scheduleAnimation();
          return;
        }

        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
      };

      const resetHoverState = () => {
        hovering = false;
        targetRadius = 0.0;
      };

      const applyInteractionMode = (nextMode) => {
        interactionMode = nextMode;
        section.dataset.fsDialMode = nextMode;

        if (interactionMode === 'hover') {
          if (onPointerEnter) section.addEventListener('mouseenter', onPointerEnter);
          if (onPointerLeave) section.addEventListener('mouseleave', onPointerLeave);
          if (onPointerMove) section.addEventListener('mousemove', onPointerMove);
        } else {
          if (onPointerEnter) section.removeEventListener('mouseenter', onPointerEnter);
          if (onPointerLeave) section.removeEventListener('mouseleave', onPointerLeave);
          if (onPointerMove) section.removeEventListener('mousemove', onPointerMove);
          resetHoverState();
        }

        syncAnimationState();
      };

      resizeHandler = () => {
        const width = media.clientWidth;
        const height = media.clientHeight;
        renderer.setSize(width, height);
        uniforms.uResolution.value.set(width, height);
        updateViewportState();
        applyInteractionMode(resolveInteractionMode());
      };

      resizeHandler();
      window.addEventListener('resize', resizeHandler);
      clock = new THREE.Clock();

      onPointerEnter = () => {
        hovering = true;
        targetRadius = 1.0;
        scheduleAnimation();
      };

      onPointerLeave = () => {
        resetHoverState();
        scheduleAnimation();
      };

      onPointerMove = (event) => {
        const rect = media.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        mouseTarget.x = (event.clientX - rect.left) / rect.width;
        mouseTarget.y = 1.0 - (event.clientY - rect.top) / rect.height;
        scheduleAnimation();
      };

      updateViewportState();
      section.classList.add('is-webgl-active');
      applyInteractionMode(resolveInteractionMode());

      if ('IntersectionObserver' in window) {
        visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isNearViewport = entry.isIntersecting;
              if (!isNearViewport && interactionMode === 'hover') {
                resetHoverState();
              }
              syncAnimationState();
            });
          },
          { rootMargin: '200px' }
        );
        visibilityObserver.observe(section);
      } else {
        isNearViewport = true;
      }

      onVisibilityChange = () => {
        isDocumentVisible =
          typeof document.visibilityState === 'undefined' ? true : document.visibilityState !== 'hidden';
        syncAnimationState();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);

      function animate() {
        if (disposed) return;
        rafId = 0;
        if (!shouldAnimate()) return;
        const delta = clock.getDelta();
        uniforms.uTime.value += delta;

        if (interactionMode === 'scroll') {
          const rect = section.getBoundingClientRect();
          const progress = clamp(
            (window.innerHeight - rect.top) / (window.innerHeight + rect.height),
            0,
            1
          );
          const easedProgress = easeInOutSine(progress);
          const horizontalDrift = Math.sin(progress * Math.PI * 2) * 0.025;
          const verticalDrift = Math.sin(progress * Math.PI * 3 + Math.PI * 0.25) * 0.018;
          const sweepRadius = Math.pow(Math.sin(progress * Math.PI), 1.2);

          mouseTarget.x = clamp(lerp(0.3, 0.7, easedProgress) + horizontalDrift, 0.18, 0.82);
          mouseTarget.y = clamp(lerp(0.58, 0.42, easedProgress) + verticalDrift, 0.22, 0.78);
          targetRadius = clamp(sweepRadius, 0, 1);
        }

        const mouseLerp = 1.0 - Math.pow(0.05, delta);
        mouseCurrent.x += (mouseTarget.x - mouseCurrent.x) * mouseLerp;
        mouseCurrent.y += (mouseTarget.y - mouseCurrent.y) * mouseLerp;
        uniforms.uMouse.value.set(mouseCurrent.x, mouseCurrent.y);

        const radiusEase =
          interactionMode === 'hover' ? (hovering ? 0.01 : 0.04) : 0.018;
        const radiusLerp = 1.0 - Math.pow(radiusEase, delta);
        currentRadius += (targetRadius - currentRadius) * radiusLerp;
        uniforms.uRadius.value = currentRadius;

        renderer.render(scene, camera);
        if (!hasRenderedFrame) {
          hasRenderedFrame = true;
          section.classList.add('is-webgl-rendered');
        }
        scheduleAnimation();
      }

      scheduleAnimation();
    };

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            if (observer) observer.disconnect();
            observer = null;
            start();
          });
        },
        { rootMargin: '200px' }
      );
      observer.observe(section);
    } else {
      start();
    }

    instances.set(section, cleanup);
  };

  const cleanupSection = (section) => {
    const cleanup = instances.get(section);
    if (!cleanup) return;
    cleanup();
  };

  const initAll = () => {
    document.querySelectorAll('[data-fs-dial-reveal]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const loaded =
      (event.target.matches && event.target.matches('[data-fs-dial-reveal]') && event.target) ||
      event.target.querySelector('[data-fs-dial-reveal]');
    if (!loaded) return;
    initSection(loaded);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const unloaded =
      (event.target.matches && event.target.matches('[data-fs-dial-reveal]') && event.target) ||
      event.target.querySelector('[data-fs-dial-reveal]');
    if (!unloaded) return;
    cleanupSection(unloaded);
  });
})();
